import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { execute } from '@/graphql/execute';
import { getPlaceholderAvatar } from '@/core/avatar';
import { getAllProjectLinksQuery } from '@/queries/project.query';
import { useQuery } from '@tanstack/react-query';
import { formatDateTime } from '../BatchList';
import { Badge } from '../ui/badge';

export function OrganizationShareLinks() {
  const { data, isLoading } = useQuery({
    queryKey: ['allProjectLinks'],
    queryFn: () => execute(getAllProjectLinksQuery),
  });

  const links = data?.allProjectLinks || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Share Links</CardTitle>
        <CardDescription>All shared project links across this organization.</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className='text-sm text-muted-foreground'>Loading share links...</div>
        ) : links.length === 0 ? (
          <div className='text-sm text-muted-foreground'>No share links have been created yet.</div>
        ) : (
          <div className='space-y-2'>
            {links.map((link) => (
              <div
                key={link.id}
                className='p-3 border rounded-xl bg-slate-50/40 dark:bg-stone-900/40 border-slate-200/70 dark:border-stone-800/70'>
                <div className='flex items-center justify-between gap-2'>
                  <div className='flex items-center gap-2 min-w-0'>
                    <Badge
                      className='dark:bg-stone-800/70 dark:border-stone-800/70 dark:text-white shrink-0'
                      variant={
                        link.status === 'ACTIVE' ? 'success' : link.status === 'EXPIRED' ? 'destructive' : 'outline'
                      }>
                      {link.status}
                    </Badge>
                    <span className='text-sm font-medium truncate'>{link.name || 'Untitled'}</span>
                  </div>
                </div>
                <div className='mt-2 text-xs text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-1'>
                  <span className='font-medium text-foreground'>{link.project?.name || 'Unknown project'}</span>
                  &middot;
                  <span>Created {formatDateTime(link.createdAt)}</span>
                  &middot;
                  <span>Expires {formatDateTime(link.expiresAt)}</span>
                  {link.createdBy && (
                    <>
                      &middot;
                      <span className='flex items-center gap-1'>
                        <img
                          src={
                            link.createdBy.avatar?.url ||
                            getPlaceholderAvatar(link.createdBy.email || link.createdBy.id)
                          }
                          alt={link.createdBy.name || link.createdBy.email || 'Unknown'}
                          className='size-4 rounded-full object-cover border border-slate-200/70 dark:border-stone-800/70'
                        />
                        {link.createdBy.name || link.createdBy.email || 'Unknown'}
                      </span>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
